import { useEffect, useMemo, useRef, useState } from "react";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import styles from "./VariantSelector.scss";
import {
  getInStockVariants,
  getVariantType,
} from "../../../utils/variantUtils";
import { VARIANT_TYPE } from "../../constants";
import {
  areAllInStockVariantsTheSamePrice,
  areAllOOSVariantsTheSamePrice,
} from "../../utils/discountPriceData";
import { useProduct } from "../../context/product/ProductContext";
import { useVariant } from "../../context/variant/VariantContext";
import SizeAndFitContainer from "../sizeAndFitContainer/SizeAndFitContainer";
import PropTypes from "prop-types";
import eventBusEmitter from "../../utils/eventBusEmitter";
import { handleModalTriggerEvent } from "../../utils/handleModalTriggerEvent";
import {
  getPrimaryVariant,
  shouldDisplayVariantSelectBasedOnVariants,
  productHasMultipleColours,
} from "../../utils/product";
import setComponentPerformance from "../../utils/setComponentPerformance";
import useEventBus from "../../hooks/useEventBus";
import { getFeature } from "../../utils/features";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { useOptionalMySizeProfile } from "../../context/mySizeProfile/MySizeProfileContext";
import { MY_SIZE_PROFILE_OPEN_MODAL_EVENT } from "../mySizeProfile/constants";
import { categoryMeasurementMap } from "../../utils/category/measurementMap";
import {
  getSizeGuide,
  getMeasurementForSize,
  extractCategoryIdFromDescription,
} from "../../utils/sizeGuideUtils";
import { extractSizeGuideId } from "../../utils/extractSizeGuideId";

const getPlaceholderText = ({ variants, variantType, getTranslation }) => {
  const label =
    variantType === VARIANT_TYPE.size
      ? getTranslation("pdp_select_size_label")
      : getTranslation("pdp_select_colour_from_label", {
          count: variants.length,
        });
  return (
    <option key={variantType} value="">
      {label}
    </option>
  );
};

const addVariantPrice = ({ variants, variant }) => {
  const displayInStockPrice = areAllInStockVariantsTheSamePrice({ variants })
    ? ""
    : ` - ${variant.price.current.text}`;

  const displayOOSPrice = areAllOOSVariantsTheSamePrice({ variants })
    ? ""
    : ` - ${variant.price.current.text}`;

  return variant.isInStock ? displayInStockPrice : displayOOSPrice;
};

const addOutOfStockLabel = ({ variant, getTranslation }) =>
  variant.isInStock ? "" : ` - ${getTranslation("pdp_status_out_of_stock")}`;

const sortVariants = ({ variants }) => {
  return variants.sort((currentVariant, nextVariant) => {
    const colourComparisonResult = currentVariant.colour.localeCompare(
      nextVariant.colour,
      undefined,
      {
        numeric: true,
      },
    );

    return colourComparisonResult === 0
      ? currentVariant.sizeOrder - nextVariant.sizeOrder
      : colourComparisonResult;
  });
};

const populateVariantSelector = ({
  variants,
  variantType,
  getTranslation,
  oosVariantsAreSelectable,
  measurementLookup,
  bodyMeasurement,
  isPrimaryDimensionEnabled,
}) => {
  const shouldCheckMeasurements =
    isPrimaryDimensionEnabled && variantType === VARIANT_TYPE.size;

  return [
    getPlaceholderText({ variants, variantType, getTranslation }),
    ...sortVariants({ variants }).map((variant, index) => {
      const measurement = measurementLookup[variant.brandSize];
      const fullLabel = variant[variantType] || "";
      const baseLabel =
        fullLabel.split(/[\s-]*(?:chest|waist|bust)/i)[0].trim() || fullLabel;
      const alreadyHasMeasurement = /\b(waist|chest|bust)[\s\-:]*\d+/i.test(
        fullLabel,
      );

      const shouldShowMeasurement =
        shouldCheckMeasurements && measurement && !alreadyHasMeasurement;
      const displayText = `${fullLabel}${
        shouldShowMeasurement ? ` - ${bodyMeasurement}: ${measurement}` : ""
      }${addVariantPrice({ variants, variant })}${addOutOfStockLabel({
        variant,
        getTranslation,
      })}`;

      const ariaLabel = shouldShowMeasurement
        ? `${baseLabel}, ${bodyMeasurement} ${measurement}${addVariantPrice({
            variants,
            variant,
          })}${addOutOfStockLabel({ variant, getTranslation })}`
        : displayText;

      return (
        <option
          key={variant.variantId}
          value={variant.variantId}
          disabled={!(oosVariantsAreSelectable || variant.isInStock)}
          data-testid={`${variantType}-${index}`}
          aria-label={ariaLabel}
        >
          {displayText}
        </option>
      );
    }),
  ];
};

const getLabelText = (variantType, getTranslation) => {
  return variantType === VARIANT_TYPE.size
    ? `${getTranslation("pdp_misc_size")}:`
    : `${getTranslation("pdp_misc_colour")}:`;
};

const updateProductImage = (selectedVariant, product, variants) => {
  if (productHasMultipleColours(product)) {
    const variant =
      selectedVariant?.colourWayId || getPrimaryVariant(variants).colourWayId;
    eventBusEmitter("setProductImage", variant);
  }
};

const shouldRenderVariantSelector = ({
  isInStock,
  product,
  oosVariantsAreSelectable,
}) => {
  return (
    shouldDisplayVariantSelectBasedOnVariants(
      product,
      oosVariantsAreSelectable,
    ) &&
    (oosVariantsAreSelectable || isInStock) &&
    !!product
  );
};

const getVariants = ({ product, variantType, oosVariantsAreSelectable }) =>
  product
    ? variantType === VARIANT_TYPE.size || oosVariantsAreSelectable
      ? product.variants
      : getInStockVariants(product.variants)
    : [];

export default function VariantSelector({
  isNoVariantSelectionError,
  setShowNoVariantSelectionError,
  isInStock,
  variantSelectorId = "variantSelector",
}) {
  const { product } = useProduct();
  const { selectedVariantId, setSelectedVariantId, selectedVariant } =
    useVariant();
  const { getTranslation, oosVariantsAreSelectable, storeCode } =
    useRuntimeConstants();
  const variantSelectorRef = useRef();
  const { areFeaturesReady } = useFeaturesReady();
  const {
    isEligible = false,
    isAuthenticated = false,
    mySizeProfile = null,
    dismissed = false,
  } = useOptionalMySizeProfile() ?? {};
  const isPrimaryDimensionEnabled = areFeaturesReady
    ? (getFeature("pdp-primary-measurement-dimension")?.isEnabled() ?? false)
    : false;
  const variantType = getVariantType({ product });
  const shouldShowMySizeProfileModal =
    isEligible && isAuthenticated && !mySizeProfile && !dismissed;
  const variants = useMemo(
    () =>
      getVariants({
        product,
        variantType,
        oosVariantsAreSelectable,
      }),
    [product, variantType, oosVariantsAreSelectable],
  );
  const [sizeGuideData, setSizeGuideData] = useState(null);
  const bodyMeasurement = categoryMeasurementMap.find(
    (item) =>
      item.id === extractCategoryIdFromDescription(product?.description),
  )?.measurement;
  const sizeGuideId = extractSizeGuideId(product?.sizeGuide);

  useEffect(() => {
    if (
      isPrimaryDimensionEnabled &&
      sizeGuideId &&
      bodyMeasurement &&
      variantType === VARIANT_TYPE.size
    ) {
      getSizeGuide(sizeGuideId)
        .then(setSizeGuideData)
        .catch(() => {
          setSizeGuideData(null);
        });
    }
  }, [isPrimaryDimensionEnabled, sizeGuideId, bodyMeasurement, variantType]);

  const measurementLookup = useMemo(() => {
    if (!sizeGuideData || !bodyMeasurement) {
      return {};
    }
    return variants.reduce((acc, variant) => {
      acc[variant.brandSize] = getMeasurementForSize(
        sizeGuideData,
        variant.brandSize,
        bodyMeasurement,
        storeCode,
      );
      return acc;
    }, {});
  }, [sizeGuideData, bodyMeasurement, variants, storeCode]);

  const showVariantSelector = shouldRenderVariantSelector({
    oosVariantsAreSelectable,
    isInStock,
    product,
  });

  const variantOptions = useMemo(
    () =>
      populateVariantSelector({
        variants,
        variantType,
        getTranslation,
        oosVariantsAreSelectable,
        measurementLookup,
        bodyMeasurement,
        isPrimaryDimensionEnabled,
      }),
    [
      variants,
      variantType,
      getTranslation,
      oosVariantsAreSelectable,
      measurementLookup,
      bodyMeasurement,
      isPrimaryDimensionEnabled,
    ],
  );

  useEffect(() => {
    if (isNoVariantSelectionError) {
      variantSelectorRef.current?.focus();
    }
  }, [isNoVariantSelectionError]);

  useEffect(() => {
    if (product) {
      updateProductImage(selectedVariant, product, product.variants);
    }
  }, [product, selectedVariant]);

  useEventBus(
    "GalleryInitialised",
    () => {
      if (product) {
        updateProductImage(selectedVariant, product, product.variants);
      }
    },
    [product, selectedVariant],
  );

  if (!showVariantSelector) {
    return null;
  }

  setComponentPerformance({ metric: "pdp:size_selector_interactive" });

  const handleSelectorMouseDown = (event) => {
    if (variantType === VARIANT_TYPE.size && shouldShowMySizeProfileModal) {
      event.preventDefault();
      handleModalTriggerEvent({
        focusOnCloseElement: variantSelectorRef.current,
        eventKey: MY_SIZE_PROFILE_OPEN_MODAL_EVENT,
      });
    }
  };

  return (
    <div data-testid="variant-selector">
      <div className={styles.sizingHelp}>
        <label htmlFor={variantSelectorId} id={styles.label}>
          {getLabelText(variantType, getTranslation)}
        </label>
        <SizeAndFitContainer />
      </div>
      <div className={styles.variantSelectorContainer}>
        <select
          key={variantSelectorId}
          id={variantSelectorId}
          ref={variantSelectorRef}
          required={isNoVariantSelectionError}
          value={selectedVariantId ? `${selectedVariantId}` : ""}
          onMouseDown={handleSelectorMouseDown}
          onChange={({ target }) => {
            setSelectedVariantId(target?.value ? parseInt(target.value) : null);
            if (target?.value) {
              setShowNoVariantSelectionError(false);
            }
          }}
        >
          {variantOptions}
        </select>
      </div>
    </div>
  );
}

VariantSelector.propTypes = {
  isNoVariantSelectionError: PropTypes.bool.isRequired,
  setShowNoVariantSelectionError: PropTypes.func.isRequired,
  isInStock: PropTypes.bool,
  variantSelectorId: PropTypes.string,
};
